import React, { useEffect, useState } from "react";
import Header from "./header";
import Footer from "./Footer";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { getLoggedUser } from "../controllers/AuthController";
import { Helmet } from "react-helmet";

const THEME_COLOR = "#0f172a";

const styles = {
  actions: {
    marginTop: "12px",
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
  },
  btn: {
    padding: "8px 14px",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "600",
  },
  primary: {
    background: THEME_COLOR,
    color: "#fff",
  },
};

const MyOrders = () => {
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userIdentifier, setUserIdentifier] = useState(null);

  /* ---------------- INIT USER ---------------- */
  useEffect(() => {
    const storedUserData = localStorage.getItem("userData");
    const loggedUser = getLoggedUser();

    let uId = null;

    if (storedUserData) {
      const u = JSON.parse(storedUserData);
      uId = u.email || u.contact;
    } else if (loggedUser) {
      uId = loggedUser;
    } else {
      navigate("/");
    }

    setUserIdentifier(uId);
  }, []);

  /* ---------------- FETCH ORDERS ---------------- */
  useEffect(() => {
    if (!userIdentifier) return;

    setLoading(true);

    fetch(
      `${process.env.REACT_APP_API_URL}/get-myorders?user_identifier=${userIdentifier}`,
    )
      .then((res) => res.json())
      .then((data) => {
        if (data?.status && Array.isArray(data.result)) {
          setOrders(data.result);
        } else {
          setOrders([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Order fetch error:", err);
        setLoading(false);
      });
  }, [userIdentifier]);

  /* ---------------- GROUP ORDERS BY PAYMENT ---------------- */
  const groupedOrders = Object.values(
    orders.reduce((acc, order) => {
      const key = order.razorpay_orderid;

      if (!acc[key]) {
        acc[key] = {
          ...order,
          products: [],
        };
      }

      acc[key].products.push({
        proname: order.proname,
        count: order.count,
        price: order.price,
        orderid: order.orderid,
      });

      return acc;
    }, {}),
  );

  /* ---------------- NOT LOGGED IN ---------------- */
  useEffect(() => {
    if (!loading && !userIdentifier) {
      navigate("/auth");
    }
  }, [loading, userIdentifier, navigate]);

  const districtsData = JSON.parse(localStorage.getItem("districts") || "[]");
  const savedCities = JSON.parse(localStorage.getItem("cities") || "[]");
  const statesData = JSON.parse(localStorage.getItem("states") || "[]");

  return (
    <>
      <Helmet>
        <title>My Orders | CGRMART</title>

        <meta
          name="description"
          content="View your order history, track purchases, and manage your CGRMART orders securely."
        />

        <meta
          name="keywords"
          content="my orders, order history, CGRMART orders, track orders, purchase history"
        />

        <meta name="author" content="CGRMART" />

        {/* Open Graph */}
        <meta property="og:title" content="My Orders | CGRMART" />
        <meta
          property="og:description"
          content="Access your order history and track your purchases easily."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />

        {/* Twitter */}
        <meta name="twitter:title" content="My Orders | CGRMART" />
        <meta
          name="twitter:description"
          content="Check your past orders and track deliveries."
        />

        {/* ⚠️ IMPORTANT (Private Page) */}
        

        <link rel="canonical" href={window.location.href} />
      </Helmet>
      <Header />
      <style>{`
        @media (max-width: 768px) {
          .my-orders-container { max-width: 100% !important; padding: 30px 15px !important; }
          .order-details-card { padding: 20px !important; }
          .order-details-icon { font-size: 48px !important; }
          .order-details-title { font-size: 22px !important; }
          .order-details-btn-group { flex-direction: column !important; }
          .order-details-btn-group button { width: 100% !important; }
        }
        @media (max-width: 480px) {
          .my-orders-container { padding: 70px 10px !important; }
          .order-details-card { padding: 15px !important; }
          .order-details-icon { font-size: 40px !important; }
          .order-details-title { font-size: 20px !important; }
        }
          .badge-button {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  height: 20px;
  display: inline-block;
  text-align: center;
}

.badge-button.success {
  background: #dcfce7;
  color: #166534;
}

.badge-button.failed {
  background: #fee2e2;
  color: #991b1b;
}

/* Responsive height for small screens */
@media (max-width: 480px) {
  .badge-button {
    height: 40px;
    line-height: 25px; /* centers text vertically */
    font-size: 14px;
  }
}
      `}</style>
      <main style={{ background: "#f8fafc", minHeight: "100vh" }}>
        <div
          style={{ maxWidth: "900px", margin: "0 auto", padding: "50px 20px" }}
          className="my-orders-container"
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              marginBottom: "20px",
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: "22px",
                fontWeight: "700",
                color: THEME_COLOR,
              }}
            >
              My Orders
            </h2>

            <div
              onClick={() => navigate(-1)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
                color: THEME_COLOR,
                marginLeft: "auto",
                fontWeight: "600",
              }}
            >
              <FaArrowLeft />
              Back
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <p>Loading orders...</p>
          ) : groupedOrders.length === 0 ? (
            <div
              className="orders-empty fade-in"
              style={{
                display: "flex",
                alignItems: "center",
                padding: "80px 0",
                margin: "0 auto",
                textAlign: "center",
                height: "500px",
                maxWidth: "500px",
                background: "linear-gradient(135deg, #e23552 0%, #0e0f0f 100%)",
                borderRadius: "24px",
                position: "relative",
                overflow: "hidden",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              {/* Animated Background Elements */}
              <div
                style={{
                  position: "absolute",
                  top: "-50%",
                  right: "-20%",
                  width: "300px",
                  height: "300px",
                  background: "rgba(255,255,255,0.1)",
                  borderRadius: "50%",
                  animation: "float 6s ease-in-out infinite",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: "-30%",
                  left: "-15%",
                  width: "250px",
                  height: "250px",
                  background: "rgba(255,255,255,0.05)",
                  borderRadius: "50%",
                  animation: "float 8s ease-in-out infinite reverse",
                }}
              />

              {/* Main Content */}
              <div
                style={{
                  position: "relative",
                  zIndex: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  textAlign: "center",
                  padding: "40px 30px",
                  color: "white",
                }}
              >
                {/* Large Animated Shopping Bag Icon */}
                <div
                  style={{
                    width: "120px",
                    height: "120px",
                    background: "rgba(255,255,255,0.2)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "30px",
                    backdropFilter: "blur(10px)",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                    animation: "pulse 2s infinite",
                  }}
                >
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="white"
                    style={{
                      filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))",
                    }}
                  >
                    <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM17 18c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2zM2 2v2h1.8l3.5 7.6-1.3 2.3c-.1.2-.1.4-.1.6 0 1.1.9 2 2 2h11.9c.2 0 .4-.1.5-.2l2.3-1.3.1-.7V7.2c0-.2-.1-.4-.2-.5L19.2 3.7C19 .9 18.1 0 17 0H5c-1.1 0-2 .9-2 2zm13.8 8.5L18 5.2l1.2 2.6h-3.4zM15 21c0-.6-.4-1-1-1s-1 .4-1 1 .4 1 1 1 1-.4 1-1zm-5 0c0-.6-.4-1-1-1s-1 .4-1 1 .4 1 1 1 1-.4 1-1z" />
                  </svg>
                </div>

                {/* Title */}
                <h2
                  style={{
                    fontSize: "32px",
                    fontWeight: "800",
                    margin: "0 0 12px 0",
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    lineHeight: "1.2",
                  }}
                >
                  No orders found
                </h2>

                {/* Subtitle */}
                <p
                  style={{
                    fontSize: "18px",
                    fontWeight: "400",
                    margin: "0 0 40px 0",
                    opacity: 0.95,
                    maxWidth: "350px",
                    lineHeight: "1.6",
                  }}
                >
                  Your shopping journey begins here. Start exploring amazing
                  products and place your first order!
                </p>

                {/* Action Button */}
                <div
                  style={{
                    display: "flex",
                    gap: "16px",
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  <button
                    onClick={() => navigate("/")}
                    style={{
                      background: "rgba(255,255,255,0.95)",
                      color: "#1E293B",
                      border: "none",
                      padding: "16px 32px",
                      borderRadius: "50px",
                      fontSize: "16px",
                      fontWeight: "700",
                      cursor: "pointer",
                      boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
                      transition: "all 0.3s ease",
                      backdropFilter: "blur(10px)",
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow = "0 12px 35px rgba(0,0,0,0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "0 8px 25px rgba(0,0,0,0.2)";
                    }}
                  >
                    🛒 Start Shopping
                  </button>
                </div>

                {/* Stats */}
                <div
                  style={{
                    marginTop: "40px",
                    display: "flex",
                    gap: "24px",
                    fontSize: "14px",
                    opacity: 0.8,
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  <div>📦 0 orders placed</div>
                  <div>⭐ 4.8 avg rating</div>
                </div>
              </div>
            </div>
          ) : (
            groupedOrders.map((order, index) => {
              const cityObj = savedCities.find(
                (city) => String(city.id) === String(order.city),
              );

              const districtObj = districtsData.find(
                (dist) =>
                  String(dist.districtid) === String(cityObj?.districtid),
              );

              const stateObj = statesData.find(
                (state) => String(state.state_id) === String(cityObj?.state_id),
              );

              const cityName = cityObj?.name || "—";
              const districtName = districtObj?.district_title || "—";
              const stateName = stateObj?.state_title || "—";
              return (
                <div
                  key={index}
                  style={{
                    background: "#fff",
                    borderRadius: "14px",
                    padding: "16px",
                    marginBottom: "16px",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
                  }}
                >
                  {/* Order Header */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: "700" }}>
                        Order Payment ID: #{order.razorpay_orderid}
                      </div>

                      <div style={{ fontSize: "13px", color: "#6b7280" }}>
                        {cityName}, {districtName}, {stateName}
                      </div>
                    </div>

                    <span
                      className={`badge-button ${order.status === "SUCCESS" ? "success" : "failed"}`}
                    >
                      {order.status === "SUCCESS"
                        ? "Order Confirmed"
                        : "Failed"}
                    </span>
                  </div>

                  {/* Products */}
                  <div style={{ marginTop: "12px" }}>
                    {order.products.map((p, i) => (
                      <div
                        key={i}
                        style={{
                          borderBottom: "1px solid #eee",
                          paddingBottom: "6px",
                          marginBottom: "6px",
                        }}
                      >
                        <div style={{ fontWeight: "600" }}>{p.proname}</div>

                        <div style={{ fontSize: "13px" }}>
                          Quantity: {p.count}
                        </div>

                        <div style={{ fontSize: "13px" }}>
                          Price: ₹{p.price}
                        </div>
                      </div>
                    ))}

                    <div
                      style={{
                        fontSize: "14px",
                        marginTop: "8px",
                        fontWeight: "700",
                      }}
                    >
                      ₹
                      {Number(order.amount).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                      })}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={styles.actions}>
                    <button
                      style={{
                        padding: "8px 14px",
                        borderRadius: "8px",
                        border: "1px solid #cbd5e1",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        navigate(`/invoice/${order.razorpay_orderid}`, {
                          state: { order },
                        })
                      }
                      disabled={order.status !== "SUCCESS"}
                    >
                      Invoice
                    </button>

                    <button
                      style={{
                        padding: "8px 14px",
                        borderRadius: "8px",
                        border: "1px solid #cbd5e1",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        navigate(`/order-details/${order.razorpay_orderid}`, {
                          state: { order },
                        })
                      }
                    >
                      View Details
                    </button>
                    <button
                      style={{ ...styles.btn, ...styles.primary }}
                      onClick={() => {
                        if (order.status === "SUCCESS") {
                          alert("Please contact customer support");
                        }
                      }}
                      disabled={order.status !== "SUCCESS"} // ✅ correct
                    >
                      Track Order
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </main>

      <Footer />
    </>
  );
};

export default MyOrders;
